import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, TextField, Typography } from "@mui/material";
import firebase from "firebase/compat/app";
import { Send as SendIcon } from "../icons";
import { SupportAgent as SupportAgentIcon } from "../icons";
import { auth, db, functions } from "../firebase";
import { brandColors } from "../theme";
import { T } from "./Translate";
import PageHeader from "./PageHeader";
import AiLinkButtons from "../components/AiLinkButtons";

const timeFmt = new Intl.DateTimeFormat("es-AR", { dateStyle: "short", timeStyle: "short" });
const formatTs = (ts) => (ts && typeof ts.toDate === "function" ? timeFmt.format(ts.toDate()) : "");

// Chat de soporte: supportChats/{uid}/messages. Primero contesta la IA
// (mode "ai", functions/aiAssistant.js); con "Hablar con una persona" o si la
// IA deriva, pasa a mode "human" y lo atiende el admin en la pestaña Chats de
// AdminPanel.js. El resumen del chat lo mantiene onSupportChatMessage.
const SupportChat = () => {
  const uid = auth.currentUser?.uid;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [chat, setChat] = useState(null);
  const [requestingHuman, setRequestingHuman] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!uid) return undefined;
    const chatRef = db.collection("supportChats").doc(uid);
    const unsubMessages = chatRef
      .collection("messages")
      .orderBy("createdAt", "asc")
      .onSnapshot(
        (snap) => {
          setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (error) => {
          console.error("Error al leer el chat de soporte:", error);
          setLoading(false);
        }
      );
    // Con la pantalla abierta, lo que responde soporte ya queda leído.
    const unsubChat = chatRef.onSnapshot(
      (snap) => {
        setChat(snap.exists ? snap.data() : null);
        if (snap.exists && snap.data().unreadByUser) {
          chatRef.update({ unreadByUser: false }).catch(() => {});
        }
      },
      () => {}
    );
    return () => {
      unsubMessages();
      unsubChat();
    };
  }, [uid]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chat?.aiTyping]);

  const humanMode = chat?.mode === "human";

  const requestHuman = async () => {
    setRequestingHuman(true);
    try {
      await functions.httpsCallable("requestHumanSupport")();
    } catch (error) {
      console.error("Error al pedir soporte humano:", error);
    } finally {
      setRequestingHuman(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const clean = text.trim().slice(0, 2000);
    if (!clean || !uid || sending) return;
    setSending(true);
    try {
      await db.collection("supportChats").doc(uid).collection("messages").add({
        from: "user",
        text: clean,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      setText("");
    } catch (error) {
      console.error("Error al enviar mensaje de soporte:", error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", flexDirection: "column" }}>
      <PageHeader title={<T>Soporte de Notiline</T>} avatarIcon={<SupportAgentIcon />} />
      <Box sx={{ flex: 1, width: "100%", maxWidth: 720, mx: "auto", px: 2, py: 2, pb: "96px" }}>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Box
              sx={{
                maxWidth: "85%",
                mb: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: "action.selected",
                color: "text.primary",
              }}
            >
              <Typography variant="body2">
                <T>Hola, contanos qué necesitás. Primero te responde el asistente con IA; si preferís, pedí hablar con una persona del equipo y te avisamos con una notificación cuando responda.</T>
              </Typography>
            </Box>
            {messages.map((m) => {
              const mine = m.from === "user";
              return (
                <Box key={m.id} sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mb: 1 }}>
                  <Box
                    sx={{
                      maxWidth: "85%",
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: mine ? brandColors.accent : "action.selected",
                      color: mine ? (brandColors.mode === "light" ? "#ffffff" : "#0a0a1a") : "text.primary",
                    }}
                  >
                    {!mine && (
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 700, opacity: 0.8 }}>
                        {m.from === "ai" ? <T>Asistente IA</T> : <T>Equipo de Notiline</T>}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {m.text}
                    </Typography>
                    <AiLinkButtons links={m.links} />
                    <Typography variant="caption" sx={{ display: "block", textAlign: "right", opacity: 0.7 }}>
                      {formatTs(m.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            {chat?.aiTyping && !humanMode && (
              <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", mb: 1 }}>
                <T>El asistente está escribiendo...</T>
              </Typography>
            )}
            {humanMode ? (
              <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", my: 2 }}>
                <T>Una persona del equipo va a responder por acá. Te avisamos con una notificación.</T>
              </Typography>
            ) : (
              messages.length > 0 && (
                <Box sx={{ textAlign: "center", my: 2 }}>
                  <Button size="small" variant="outlined" onClick={requestHuman} disabled={requestingHuman} sx={{ textTransform: "none", borderRadius: 999 }}>
                    <T>Hablar con una persona</T>
                  </Button>
                </Box>
              )
            )}
          </>
        )}
        <div ref={endRef} />
      </Box>

      <Box
        component="form"
        onSubmit={handleSend}
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          py: 1,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, maxWidth: 720, mx: "auto", px: 2 }}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) handleSend(e);
            }}
            placeholder="Escribí tu mensaje..."
            size="small"
            fullWidth
            multiline
            maxRows={4}
            inputProps={{ maxLength: 2000 }}
          />
          <IconButton type="submit" disabled={!text.trim() || sending} aria-label="Enviar" sx={{ color: brandColors.accent }}>
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default SupportChat;
